/**
 * 워크스페이스 좌측 메뉴 선언 배열 SSoT — 기존 web-workspace 라우트 meta(icon·title·hideInMenu)에서 이관.
 * 메뉴에 노출되는 항목만 정의하며, 상세·생성 등 hideInMenu 라우트는 여기 없다(라우터에만 존재).
 * 노출은 항목의 `feature` 가 세션 기능 목록에 있을 때만(`useWorkspaceNav`, specs/064).
 * 활성 판정은 `match` prefix — 하위 상세 경로(/companies/12)도 같은 메뉴가 활성이 된다.
 */
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  CirclePlay,
  ClipboardList,
  CreditCard,
  House,
  LayoutDashboard,
  Map,
  Search,
  Store,
  type LucideIcon,
} from 'lucide-react';

import { ROUTES } from '@/config/routes';
import type { WorkspaceFeature } from '@/config/workspace-features';

export interface WorkspaceNavItem {
  key: string;
  label: string;
  description: string;
  to: string;
  icon: LucideIcon;
  /** 활성 판정 prefix(기본 to). */
  match?: string;
  /** 이 메뉴가 속한 기능 — 세션 기능 목록에 있을 때만 노출(specs/064). */
  feature: WorkspaceFeature;
}

export const WORKSPACE_NAV: readonly WorkspaceNavItem[] = [
  // 기업 회원(specs/065) — 첫 메뉴가 첫 화면이 된다(WorkspaceEntryRedirect).
  {
    key: 'home',
    feature: 'COMPANY_HOME',
    label: '홈',
    description: '다음 할 일과 회사 정보 완성도를 봅니다.',
    to: ROUTES.workspace.home,
    icon: House,
  },
  {
    // 기업 셀프 묶음 메뉴 — 화면 라우트는 판정 기능(COMPANY_JUDGEMENT)도 함께 요구한다(specs/069)
    key: 'my-announcements',
    feature: 'COMPANY_HOME',
    label: '공고 찾기',
    description: '우리 회사 정보로 판정한 지원사업을 보고, 질문에 답해 판정을 좁힙니다.',
    to: ROUTES.workspace.myAnnouncements,
    icon: Search,
  },
  {
    // 기업 성장 로드맵(specs/071) — 화면이 ROADMAP 기능도 확인한다
    key: 'my-roadmap',
    feature: 'COMPANY_HOME',
    label: '성장 로드맵',
    description: '업력에 따라 다음에 노릴 사업을 이어 봅니다.',
    to: ROUTES.workspace.myRoadmap,
    icon: Map,
  },
  {
    key: 'my-company',
    feature: 'COMPANY_HOME',
    label: '우리 회사',
    description: '지원사업 자격 확인에 쓰는 회사 정보를 관리합니다.',
    to: ROUTES.workspace.myCompany,
    icon: Store,
  },
  {
    key: 'announcements',
    feature: 'ANNOUNCEMENT_FINDER',
    label: '공고 찾기',
    description: '조건으로 좁히고 질문으로 찾습니다 — 검색어가 없으면 최신 공고 목록입니다.',
    to: ROUTES.workspace.announcements,
    icon: Search,
  },
  {
    key: 'dashboard',
    feature: 'TASKS',
    label: '할 일',
    description: '담당 업체별로 지금 챙길 것과 다가오는 공고를 확인합니다.',
    to: ROUTES.workspace.dashboard,
    icon: LayoutDashboard,
  },
  {
    key: 'calendar',
    feature: 'CALENDAR',
    label: '캘린더',
    description: '공고 일정을 타임라인으로 훑어보고 업체별 일정을 확인합니다.',
    to: ROUTES.workspace.calendar,
    icon: CalendarDays,
  },
  {
    key: 'companies',
    feature: 'MULTI_COMPANY',
    label: '내 업체',
    description: '담당 고객 업체의 프로필과 지원사업 판정을 관리합니다.',
    to: ROUTES.workspace.companies,
    icon: Building2,
    match: `${ROUTES.workspace.companies}`,
  },
  {
    key: 'consultant-announcements',
    feature: 'CONSULTANT_ANNOUNCEMENT',
    label: '컨설턴트 공고',
    description: '경영지도사 본인이 참여할 수 있는 모집 공고입니다.',
    to: ROUTES.workspace.consultantAnnouncements,
    icon: ClipboardList,
  },
  {
    key: 'certifications',
    feature: 'CERTIFICATION',
    label: '기업 인증 안내',
    description: '인증·확인·인정 제도의 절차와 준비자료를 확인합니다.',
    to: ROUTES.workspace.certifications,
    icon: BadgeCheck,
  },
  {
    key: 'subscription',
    feature: 'SUBSCRIPTION',
    label: '구독',
    description: '요금제·결제수단·해지를 관리합니다.',
    to: ROUTES.workspace.subscription,
    icon: CreditCard,
  },
  {
    key: 'guide',
    feature: 'GUIDE',
    label: '사용 안내',
    description: '1~2분 영상으로 주요 기능 사용법을 봅니다.',
    to: ROUTES.workspace.guide,
    icon: CirclePlay,
  },
] as const;
